import React from 'react'
import Stack from '@mui/material/Stack'
import { useFormContext, useWatch } from 'react-hook-form'
import type { StackProps } from '@mui/material/Stack'
import { IntegerField } from '../IntegerField'
import { SelectField } from '../SelectField'
import { StartLabelField } from './StartLabelField'
import type { IntegerFieldProps } from '../IntegerField'
import type { SelectFieldProps } from '../SelectField'

enum OperatorsValuesEnum {
    BETWEEN = 'range',
    EQUAL_TO = 'equals',
    GREATER_THAN = 'above',
    LESS_THAN = 'below',
    NOT_EQUAL_TO = 'notequal',
}

type Props = {
    label: string
    selectProps: SelectFieldProps
    lowProps: IntegerFieldProps
    highProps: IntegerFieldProps
} & StackProps

/**
 *  Must be in the grid container to display properly
 * */
export const StartLabelRangeStack: React.VFC<Props> = ({
    label,
    selectProps,
    lowProps,
    highProps,
    ...stackProps
}: Props) => {
    const { getValues, resetField, trigger } = useFormContext()
    const selectedValue = useWatch({ name: selectProps.name }) as string
    const showLow = !!selectedValue
    const showHigh = selectedValue ? selectedValue === OperatorsValuesEnum.BETWEEN : false

    React.useEffect(() => {
        const triggeredNames = [selectProps.name]

        if (!showLow) {
            const priorLowValue = getValues(lowProps.name) as string
            if (priorLowValue) {
                resetField(lowProps.name, { defaultValue: '' })
                triggeredNames.push(lowProps.name)
            }
        }

        if (!showHigh) {
            const priorHighValue = getValues(highProps.name) as string
            if (priorHighValue) {
                resetField(highProps.name, { defaultValue: '' })
                triggeredNames.push(lowProps.name)
            }
        }

        void trigger(triggeredNames)
    }, [
        selectedValue, // Required for immediate validation, not onBlur
        getValues,
        resetField,
        trigger,
        showLow,
        showHigh,
        selectProps.name,
        lowProps.name,
        highProps.name,
    ])

    return (
        <StartLabelField label={label} name={selectProps.name}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} {...stackProps}>
                <SelectField {...selectProps} />
                {showLow && <IntegerField {...lowProps} />}
                {showHigh && <IntegerField {...highProps} />}
            </Stack>
        </StartLabelField>
    )
}

export default StartLabelRangeStack
export type { Props as StartLabelRangeStackProps }
